import { toInteger } from 'es-toolkit/compat';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import {
  createSearchCriteria,
  isEmptySearchCriteria,
  postingSearchCriteriaSchema,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import {
  ensureOptionalString,
  ensurePositiveNumber,
  yupObject,
} from 'shared/utils/YupUtils';
import { InferType, mixed, number, string } from 'yup';

export type LoadboardPostingType =
  | 'available'
  | 'saved-loads'
  | 'booked'
  | 'suggested'
  | 'requested';

const POSTINGS_PAGE_TABS = [
  'available',
  'booked',
  'suggested',
  'requested',
  'saved-search',
  'saved-loads',
  'private-loadboard',
] as const;

export type PostingsPageTab = (typeof POSTINGS_PAGE_TABS)[number];

function normalizePostingsTab(value?: PostingsPageTab): PostingsPageTab {
  switch (value) {
    case 'booked':
    case 'suggested':
    case 'requested':
    case 'saved-search':
    case 'saved-loads':
    case 'private-loadboard':
      return value;
    default:
      return 'available';
  }
}

export type LoadSearchSource =
  // utm_medium
  | 'Search'
  | 'Similar Loads'
  | 'Load Alert Nudging Dialog'
  | 'Load Alert Card' // only for webview
  | 'Load Alert List'
  | 'Advanced Search'
  | 'Search Results'
  | 'Nearby Shipper Loads'

  // utm_content
  | 'Unposted Load'
  | 'Load Alert'
  | 'Edit Load Alert Button'
  | 'Intrastate State Selection Dialog';

type LoadAlertActionParam = 'update' | 'save-search' | 'search' | 'create';

const transformCurrentPageSize = (value?: number | string) => {
  if (!value) {
    return !MobileAppBridge.isInjected() ? 50 : 25;
  }
  return Math.min(Math.max(0, toInteger(value)), 100);
};

export type PostingsPageParamsDTO = InferType<typeof postingsPageParamsSchema>;
export const postingsPageParamsSchema = yupObject({
  load_alert: mixed<LoadAlertActionParam>().optional(),
  shipper_guid: string().defined(),
  pickup_city: string().nullable().defined(),
  pickup_state: string().nullable().defined(),
  delivery_city: string().nullable().defined(),
  delivery_state: string().nullable().defined(),
  utm_source: string()
    .nullable()
    .optional()
    .transform((value) => {
      // utm_source may come as array like ?utm_source=SMS&utm_source=CTMS Mobile
      if (Array.isArray(value)) {
        return value[0];
      }

      return value;
    }),
  utm_medium: mixed<LoadSearchSource>().optional(),
  utm_content: mixed<LoadSearchSource>().optional(),
  page_sort: mixed<string | string[]>()
    .default('posted_to_loadboard_at,desc')
    .required(),
  current_page: number()
    .default(ensurePositiveNumber)
    .transform(ensurePositiveNumber)
    .required(),
  current_tab: string()
    .oneOf(POSTINGS_PAGE_TABS)
    .transform(normalizePostingsTab)
    .default(normalizePostingsTab)
    .required(),
  current_saved_search_guid: string().transform(ensureOptionalString),
  current_load_guid: string().transform(ensureOptionalString),
  back_url: string().transform(ensureOptionalString),
  current_page_size: number()
    .transform(transformCurrentPageSize)
    .default(transformCurrentPageSize)
    .required(),
  search_criteria: postingSearchCriteriaSchema,
});

export const createExternalCriteria = ({
  pickup_city,
  pickup_state,
  delivery_city,
  delivery_state,
}: PostingsPageParamsDTO) => {
  const criteria = createSearchCriteria({});

  if (pickup_city || pickup_state) {
    criteria.pickup_venues = [
      {
        latitude: null,
        longitude: null,
        zip: null,
        city: pickup_city,
        state: pickup_state?.toLowerCase(),
      },
    ];
  }

  if (delivery_city || delivery_state) {
    criteria.delivery_venues = [
      {
        latitude: null,
        longitude: null,
        zip: null,
        city: delivery_city,
        state: delivery_state?.toLowerCase(),
      },
    ];
  }

  return criteria;
};

export const isEmptyCriteria = (params: PostingsPageParamsDTO) => {
  return (
    !(
      params.current_tab === 'available' ||
      params.current_tab === 'private-loadboard'
    ) || isEmptySearchCriteria(params.search_criteria)
  );
};

export const convertPostingPageParamsToQuery = (
  params: Record<string, unknown>,
) => {
  const postingPageParamsQuery = postingsPageParamsSchema.cast();
  return { ...postingPageParamsQuery, ...params };
};
